/**
 * Phase 48 (2026-07-29) - 12-Month Income Combo Chart.
 *
 * Recharts <ComposedChart> with:
 *   - 12 vertical bars (past 11 months semi-transparent, current month solid).
 *   - Smooth 3-month rolling-average trend line overlaid.
 *   - Faint horizontal gridlines, no vertical gridlines, no axis lines.
 *   - Custom tooltip with month name + income + rolling avg.
 *
 * Colors resolve from theme tokens so the chart re-themes correctly.
 */
import { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

/** Month label from "YYYY-MM" -> "Jul" */
function monthShort(iso) {
  if (!iso) return "";
  const m = parseInt(iso.slice(5, 7), 10);
  return MONTH_LABELS[m - 1] || "";
}

/** Full month name + year: "July 2026" */
function monthFull(iso) {
  if (!iso) return "";
  const y = iso.slice(0, 4);
  const m = parseInt(iso.slice(5, 7), 10);
  const full = ["January","February","March","April","May","June",
                 "July","August","September","October","November","December"][m - 1];
  return `${full} ${y}`;
}

/** Tooltip renderer (module-scope) */
function IncomeTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      className="rounded-md border px-2 py-1.5 text-xs shadow-md"
      style={{
        backgroundColor: "var(--bg-surface)",
        borderColor: "var(--border-default)",
        color: "var(--text-primary)",
        minWidth: 150,
      }}
      data-testid="income-combo-tooltip"
    >
      <p className="font-medium mb-0.5">{monthFull(row.month)}</p>
      <p>
        <span style={{ color: "var(--text-muted)" }}>Income:</span>{" "}
        <span className="tabular-nums font-medium">{fmtMoney(row.income)}</span>
      </p>
      {row.rolling_3m !== null && row.rolling_3m !== undefined && (
        <p>
          <span style={{ color: "var(--text-muted)" }}>3-month avg:</span>{" "}
          <span className="tabular-nums font-medium">{fmtMoney(row.rolling_3m)}</span>
        </p>
      )}
    </div>
  );
}

export default function IncomeComboChart({
  series = [],
  currentMonthISO = null,
  height = 140,
  isEmpty = false,
}) {
  const data = useMemo(() => (series || []).map((r) => ({
    month: r.month,
    label: monthShort(r.month),
    income: Number(r.income || 0),
    rolling_3m: r.rolling_3m === null || r.rolling_3m === undefined
      ? null : Number(r.rolling_3m),
  })), [series]);

  const currentIdx = useMemo(() => {
    if (!currentMonthISO) return data.length - 1;
    const idx = data.findIndex((r) => r.month === currentMonthISO);
    return idx >= 0 ? idx : data.length - 1;
  }, [data, currentMonthISO]);

  if (!data.length) {
    return (
      <div
        style={{ height }}
        className="rounded-md border border-dashed flex items-center justify-center text-xs"
        data-testid="income-combo-empty"
      >
        <span style={{ color: "var(--text-muted)" }}>No data</span>
      </div>
    );
  }

  return (
    <div
      className="relative"
      style={{ height }}
      data-testid="income-combo-chart"
      data-empty={isEmpty ? "true" : "false"}
    >
      {isEmpty && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
          data-testid="income-combo-empty-overlay"
        >
          <span
            className="text-xs px-2 py-1 rounded-md"
            style={{
              color: "var(--text-muted)",
              backgroundColor: "var(--bg-hover, rgba(255,255,255,0.75))",
            }}
          >
            No revenue recorded in the last 12 months
          </span>
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 6, right: 4, bottom: 0, left: -12 }}
          barCategoryGap="30%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border-default)"
            strokeOpacity={0.4}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 9,
              fill: "var(--text-muted)",
              letterSpacing: "0.10em",
              textTransform: "uppercase",
            }}
            interval={0}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: "var(--text-muted)" }}
            tickFormatter={fmtCompact}
            width={44}
          />
          <Tooltip content={<IncomeTooltip />} cursor={{ fill: "var(--bg-hover, rgba(0,0,0,0.04))" }} />
          <Bar
            dataKey="income"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          >
            {data.map((_, i) => (
              <Cell
                key={`c-${i}`}
                fill="var(--chart-bar-solid, #1F4FA3)"
                fillOpacity={i === currentIdx ? 1 : 0.4}
              />
            ))}
          </Bar>
          <Line
            type="monotone"
            dataKey="rolling_3m"
            stroke="var(--chart-line-solid, #0F1E3D)"
            strokeWidth={1.5}
            dot={{ r: 2.5, fill: "var(--chart-line-solid, #0F1E3D)", stroke: "var(--bg-surface, #FFFFFF)", strokeWidth: 1 }}
            isAnimationActive={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
